<template>
  <span class="actuals-help-wrap">
    <b-button
      size="sm"
      variant="success"
      title="About the Booking On-Time Performance Report"
      aria-label="Help"
      @click="show_help = true"
    >
      <i class="fa fa-info-circle"></i>
    </b-button>

    <b-modal
      v-model="show_help"
      title="Booking On-Time Performance Report"
      size="xl"
      ok-only
      scrollable
    >
      <div class="actuals-help-body">
        <h4>What this page shows</h4>
        <p>
          The planned times on each movement compared against the <b>actual</b> times calculated by actuals
          processing. Each milestone gets three columns: Planned, Actual, and Diff.
        </p>
        <ul>
          <li>The date at the top is the movement's <b>start day</b> — the day it departs. An overnight run that leaves one evening and returns the next morning appears on the day it left.</li>
          <li>One row per <b>vehicle</b>, not per booking: a movement covered by three coaches produces three rows, each with that vehicle's own actual times.</li>
          <li>Cancelled bookings and cancelled movements are left out.</li>
          <li>Start and Finish show date and time; the milestones in between show time only, in your company timezone.</li>
        </ul>

        <hr/>

        <h4>Where the Actual times come from</h4>
        <p>
          This page mixes two sources, and which one you're looking at depends on the column:
        </p>
        <ul>
          <li><b>Start and Finish prefer the driver's HOS times.</b> Start takes the driver's HOS clock-in when there is one; Finish takes the clock-out. Only if HOS is missing does it fall back to the vehicle — engine on/off near the base, then a GPS-confirmed departure from (or arrival at) the base, then the telematics route time. The first source available wins, in that order.</li>
          <li><b>Position, Pickup, Arrival, Leave, and Back are vehicle telematics (GPS) only.</b> Driver duty times are never used for these.</li>
        </ul>
        <p>
          Because Start and Finish usually come from the driver while everything between them comes from the
          vehicle, a row can show an on-time Start and a late Position: the driver logged on when planned, the
          coach left the yard late. The Actuals page records which source won for Start and Finish on each
          movement, so that's the place to confirm a specific row.
        </p>
        <p>How a GPS time gets picked for a stop:</p>
        <ul>
          <li><b>Arrival</b> — the first position report within {{ radius_label }} of the planned stop with the vehicle stopped, or inside the ELD-configured geofence for that address when your company has that option enabled.</li>
          <li><b>Departure</b> — the first position report showing the vehicle moving away from the stop.</li>
          <li>The search starts an hour before the planned time and widens if nothing matches — later milestones get more retries than earlier ones, since delay accumulates across the day.</li>
        </ul>
        <p>
          So a blank Actual usually means the vehicle never reported a stopped position near the planned
          coordinates — missing or wrong stop lat/lon, a gap in vehicle GPS reporting, or the wrong vehicle
          assigned.
        </p>

        <hr/>

        <h4>Column by column</h4>
        <table class="actuals-help-col-table">
          <tr>
            <th>Column</th>
            <th>Planned</th>
            <th>Actual</th>
          </tr>
          <tr>
            <td><b>Start</b></td>
            <td>Planned departure from the start base.</td>
            <td>Driver HOS clock-in. Falls back to engine-on, then GPS departure from the base, then route start.</td>
          </tr>
          <tr>
            <td><b>Position</b></td>
            <td>Planned arrival at the pickup (spot time).</td>
            <td>Vehicle GPS arrival at the pickup location.</td>
          </tr>
          <tr>
            <td><b>Pickup</b></td>
            <td>Planned departure from the pickup.</td>
            <td>Vehicle GPS departure from the pickup location.</td>
          </tr>
          <tr>
            <td><b>Arrival</b></td>
            <td>Planned arrival at the destination.</td>
            <td>Vehicle GPS arrival at the destination.</td>
          </tr>
          <tr>
            <td><b>Leave</b></td>
            <td>Planned departure from the destination.</td>
            <td>Vehicle GPS departure from the destination.</td>
          </tr>
          <tr>
            <td><b>Back</b></td>
            <td>Planned arrival back at the pickup.</td>
            <td>Vehicle GPS arrival back at the pickup — or departure from it, for companies configured that way.</td>
          </tr>
          <tr>
            <td><b>Finish</b></td>
            <td>Planned arrival at the finish base.</td>
            <td>Driver HOS clock-out. Falls back to engine-off, then GPS arrival at the base, then route finish.</td>
          </tr>
        </table>
        <p class="actuals-help-note">
          Milestones that a movement doesn't have (no destination leg, for instance) stay blank. Actuals that land
          out of sequence are cleared rather than shown — if Arrival somehow computes earlier than Pickup, it's
          blanked — so gaps in the middle of a row can mean "rejected as impossible", not "never happened".
        </p>

        <hr/>

        <h4>Diff, Early Limit, and Late Limit</h4>
        <ul>
          <li><b>Diff</b> is Actual minus Planned, in minutes. A positive number means the milestone happened <b>after</b> planned (late); a negative number means <b>before</b> planned (early).</li>
          <li><b>Early Limit</b> / <b>Late Limit</b> set how big a Diff has to be before it's flagged. A Diff has to <b>exceed</b> the limit — with a 10m late limit, 11 minutes late is shaded red and exactly 10 is not.</li>
          <li>Changing either limit re-shades and re-buckets the rows already loaded; it does not re-query the day.</li>
        </ul>

        <hr/>

        <h4>Filters</h4>
        <ul>
          <li>The first chip shows <b>rows displayed / rows for the day</b>.</li>
          <li><b>On Time</b> — no milestone on the row crossed either limit.</li>
          <li><b>Early</b> / <b>Late</b> — <i>any</i> milestone on the row crossed that limit. A row that ran early to one stop and late to another counts in both, so the chip counts can add up to more than the total.</li>
          <li>The chip counts describe the whole day, not your current filter selection — they stay put as you narrow the table so you can see what you're filtering out of.</li>
          <li><b>Type</b> narrows to contract or private hire; <b>Company</b> appears when the day spans more than one.</li>
          <li><b>Filter Results</b> matches on charter ID, vehicle, or driver.</li>
          <li>Click any column heading to sort by it; click again to reverse.</li>
        </ul>
        <p class="actuals-help-note">
          <b>Download</b> exports the full selected day. Unlike the on-screen chips, it ignores the type, company,
          status, and text filters.
        </p>

        <hr/>

        <h4>Links out</h4>
        <ul>
          <li><b>MvmtID</b> — opens the Actuals page for that booking, where you can see the processing status, which source each actual came from, and reprocess it.</li>
        </ul>
      </div>
    </b-modal>
  </span>
</template>

<script>
export default {
  name: 'actuals_perf_help',

  data() {
    return {
      show_help: false,
      // process_actuals.py's _distance_km_threshold: a fixed 0.3km for CM, not a tenant setting
      radius_label: '300 meters',
    }
  },
}
</script>

<style scoped>
.actuals-help-wrap {
  display: inline-block;
  margin-left: 8px;
}
.actuals-help-body {
  font-size: 15px;
  line-height: 1.5;
  text-align: left;
}
.actuals-help-body h4 {
  font-size: 17px;
  font-weight: bold;
  margin-bottom: 8px;
}
.actuals-help-body p {
  margin-bottom: 10px;
}
.actuals-help-body ul {
  padding-left: 20px;
  margin-bottom: 10px;
}
.actuals-help-body li {
  margin-bottom: 6px;
}
.actuals-help-note {
  font-size: 14px;
  font-style: italic;
  color: #6c757d;
}
.actuals-help-col-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 10px;
}
.actuals-help-col-table th,
.actuals-help-col-table td {
  border: 1px solid #ddd;
  padding: 6px 8px;
  vertical-align: top;
  font-size: 14px;
}
.actuals-help-col-table th {
  background-color: #f1f3f5;
  text-align: left;
}
.actuals-help-col-table td:first-child {
  white-space: nowrap;
}
</style>
